import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { FieldError } from "react-hook-form";
import { AllSection, getAllSectionMillNames } from "../models/AllSection";
import { getAllSections } from "../repositories/AllSectionRepository";

export const useAllSections = () =>
	useQuery<AllSection[], Error>({
		queryKey: ["allSections"],
		queryFn: ({ signal }) => getAllSections(signal),
	});

interface AllSectionOption {
	sectionId: number | null;
	sectionName: string;
	millName: string;
	section: AllSection | null;
}

type Props = {
	value: number | null;
	onChange: (sectionId: number | null, section: AllSection | null) => void;
	label?: string;
	size?: "small" | "medium";
	disabled?: boolean;
	required?: boolean;
	autoFocus?: boolean;
	fieldError?: FieldError;
	millNames?: string[];
	allOptionLabel?: string;
};

export const AllSectionSelector = ({
	value,
	onChange,
	label = "Section",
	size = "small",
	disabled = false,
	required = false,
	autoFocus = false,
	fieldError,
	millNames,
	allOptionLabel,
}: Props) => {
	const { data, isLoading, isError, error } = useAllSections();

	const sections = data ?? [];
	const scoped =
		millNames === undefined
			? sections.map((section) => ({
					sectionId: section.SectionId,
					sectionName: section.SectionName,
					millName: "",
					section,
				}))
			: millNames.flatMap((millName) =>
					sections
						.filter((section) =>
							getAllSectionMillNames(section).includes(millName),
						)
						.map((section) => ({
							sectionId: section.SectionId,
							sectionName: section.SectionName,
							millName,
							section,
						})),
				);

	const options: AllSectionOption[] =
		allOptionLabel === undefined
			? scoped
			: [
					{
						sectionId: null,
						sectionName: allOptionLabel,
						millName: "",
						section: null,
					},
					...scoped,
				];

	const selectedOption =
		options.find((option) => option.sectionId === value) ?? null;
	const isGrouped = (millNames?.length ?? 0) > 1;

	const helperText = isError
		? (error?.message ?? "Unable to load sections.")
		: fieldError?.message;

	return (
		<Autocomplete
			size={size}
			options={options}
			value={selectedOption}
			loading={isLoading}
			loadingText="Loading sections..."
			noOptionsText={isError ? "Unable to load sections" : "No sections found"}
			disabled={disabled || isError}
			autoHighlight
			clearOnEscape
			disableClearable={allOptionLabel === undefined}
			groupBy={isGrouped ? (option) => option.millName || "" : undefined}
			getOptionLabel={(option) => option.sectionName}
			isOptionEqualToValue={(option, selected) =>
				option.sectionId === selected.sectionId
			}
			renderOption={(props, option) => {
				const { key, ...liProps } = props as typeof props & { key: string };
				const caption =
					isGrouped || option.section === null
						? null
						: getAllSectionMillNames(option.section).join(", ");
				return (
					<li key={key} {...liProps}>
						<div>
							<div>{option.sectionName}</div>
							{caption && (
								<div style={{ color: "#5B6670", fontSize: "0.75rem" }}>
									{caption}
								</div>
							)}
						</div>
					</li>
				);
			}}
			onChange={(_, option) =>
				onChange(option?.sectionId ?? null, option?.section ?? null)
			}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					autoFocus={autoFocus}
					error={isError || !!fieldError}
					helperText={helperText}
					margin="none"
					inputProps={{
						...params.inputProps,
						"data-test-id": "section-selector",
					}}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading && <CircularProgress color="inherit" size={18} />}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			sx={{ minWidth: 180 }}
		/>
	);
};
